import { useState } from 'react';
import { useParams, useLoaderData } from 'react-router-dom';
import { redirectErrorPage } from '../../utils/errors';
import ChartComponent from '../../components/ChartComponent';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../utils/texts';
import Intro from '../../components/common/Intro/Intro';
import SummarySection from '../../components/common/SummarySection/SummarySection';
import { format } from 'date-fns';
import { cs, enUS } from 'date-fns/locale';
import { Colors } from '../../Constants';
import Section from '../../components/common/Section/Section';
import MemberCardWrapper from '../../components/common/MemberCardWrapper/MemberCardWrapper';
import { useVoteColors } from '../../utils/hooks';
import { VotingDetailResponseType } from '../../types';

const VotingDetail = () => {
    const [activeTab, setActiveTab] = useState<number | undefined>(undefined);
    const { zastupitelstvoId, municipalitaId } = useParams();
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language;

    const voteData = useLoaderData() as VotingDetailResponseType;

    const voteOptions = { ano: 1, ne: 2, 'zdržel se': 3, nehlasoval: 4, 'nepřít.': 5, omluven: 6, tajná: 7 };

    const voteColors = useVoteColors();

    if (voteData !== undefined) {
        if (JSON.stringify(voteData) === '{"error":"neexistujici ID"}') {
            redirectErrorPage(municipalitaId, zastupitelstvoId);
        } else {
            let summaryVotesEntries = Object.entries(voteData?.sumarizace ?? {});
            summaryVotesEntries.sort(
                (a, b) => Object.keys(voteOptions).indexOf(a[0]) - Object.keys(voteOptions).indexOf(b[0])
            );
            let summaryVotesLabels = summaryVotesEntries.map(([label]) => label);
            let summaryVotesValues = summaryVotesEntries.map(([, value]) => value);

            let datasetsDataSummaryVotes = summaryVotesValues.map((item, index) => ({
                type: 'bar',
                label: capitalizeFirstLetter(summaryVotesLabels[index]),
                backgroundColor: voteColors[summaryVotesLabels[index] as keyof typeof voteColors],
                data: [item]
            }));

            let summaryVotesData = {
                labels: [''],
                datasets: datasetsDataSummaryVotes
            };

            const handleBarClick = (evt: any, item: { datasetIndex: number }[]) => {
                if (item[0] !== undefined) {
                    const option_id = item[0].datasetIndex;
                    setActiveTab(undefined);
                    setTimeout(() => {
                        setActiveTab(option_id);
                        const element = document.getElementById(`${option_id}-datasetIndex-0`);
                        if (element) {
                            setTimeout(() => element?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 330);
                        }
                    }, 0);
                }
            };

            const handleBarHover = (evt: { native: { target: { style: { cursor: string } } } }, item: undefined[]) => {
                if (item[0] !== undefined) {
                    evt.native.target.style.cursor = 'pointer';
                } else {
                    evt.native.target.style.cursor = 'default';
                }
            };

            const stackedOptions = {
                indexAxis: 'y',
                maintainAspectRatio: false,
                aspectRatio: 0.8,
                plugins: {
                    tooltips: {
                        mode: 'index',
                        intersect: false
                    },
                    legend: {
                        labels: {
                            generateLabels: (chart: { data: { datasets: any } }) => {
                                const datasets = chart.data.datasets;
                                return datasets.map(
                                    (data: { label: any; data: any[]; backgroundColor: any }, i: any) => ({
                                        text: `${data.label} (${data.data[0]})`,
                                        fillStyle: data.backgroundColor,
                                        strokeStyle: 'white',
                                        datasetIndex: 0,
                                        index: i
                                    })
                                );
                            }
                        },
                        onHover: function (
                            evt: { native: { target: { style: { cursor: string } } } },
                            item: { datasetIndex: number },
                            legend: { chart: { tooltip: any; data: { datasets: any[] }; update: () => void } }
                        ) {
                            const tooltip = legend.chart.tooltip;
                            if (tooltip.getActiveElements().length > 0) {
                                tooltip.setActiveElements([], { x: 0, y: 0 });
                            }

                            tooltip.setActiveElements([{ datasetIndex: item.datasetIndex, index: 0 }], { x: 0, y: 0 });

                            evt.native.target.style.cursor = 'pointer';
                            legend.chart.data.datasets.forEach((i: { backgroundColor: string }, id: number) => {
                                if (id !== item.datasetIndex) {
                                    i.backgroundColor += '30';
                                }
                            });
                            legend.chart.update();
                        },
                        onLeave: function (
                            evt: any,
                            item: { datasetIndex: number },
                            legend: { chart: { data: { datasets: any[] }; update: () => void } }
                        ) {
                            legend.chart.data.datasets.forEach((i: { backgroundColor: string | any[] }, id: number) => {
                                if (id !== item.datasetIndex) {
                                    i.backgroundColor = i.backgroundColor.slice(0, -2);
                                }
                            });
                            legend.chart.update();
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: true
                    },
                    y: {
                        stacked: true,
                        position: 'right'
                    }
                },
                onHover: handleBarHover,
                onClick: handleBarClick
            };

            const dateTime = voteData?.datum_cas.split(' ');
            dateTime[0] = format(new Date(dateTime[0].split('.').reverse().join('-')), 'd. MMMM yyyy', {
                locale: currentLocale === 'cs' ? cs : enUS
            });

            const result = voteData?.vysledek === 'přijato' ? true : false;
            const resultItem = {
                icon: result ? 'check_box' : 'disabled_by_default',
                text: `${t('proposal-was')} ${t(result ? t('proposal-accepted') : t('proposal-rejected'))}`,
                color: result ? Colors.success : Colors.error
            };

            const summarySectionItems = [];
            if (voteData?.proceduralni_hlasovani === 1) {
                summarySectionItems.push(
                    {
                        icon: 'settings',
                        text: t('procedural-option'),
                        highlightSubString: t('procedural-option')
                    },
                    {
                        text: t('procedural-tooltip')
                    }
                );
            }
            if (voteData?.validni_hlasovani === 0) {
                summarySectionItems.push(
                    {
                        icon: 'unpublished',
                        text: t('invalid-option'),
                        highlightSubString: t('invalid-option')
                    },
                    {
                        text: t('invalid-tooltip')
                    }
                );
            }
            if (voteData?.tajne_hlasovani === 1) {
                summarySectionItems.push(
                    {
                        icon: 'lock',
                        text: t('secret-option'),
                        highlightSubString: t('secret-option')
                    },
                    {
                        text: t('secret-tooltip')
                    }
                );
            }

            summarySectionItems.push(
                {
                    icon: 'calendar_today',
                    text: `${dateTime[0]}${dateTime[1]}`,
                    highlightSubString: `${dateTime[0]}`,
                    mutedSubString: `${dateTime[1]}`
                },
                {
                    icon: resultItem.icon,
                    text: resultItem.text,
                    iconAndHighlightColor: resultItem.color,
                    highlightSubString: result ? t('proposal-accepted') : t('proposal-rejected')
                },
                {
                    icon: 'group',
                    text: `${voteData?.pritomno} ${t('members-present')}`,
                    highlightSubString: `${voteData?.pritomno}`
                }
            );
            if (voteData?.soubor !== '') {
                summarySectionItems.push({
                    icon: 'draft',
                    text: `${capitalizeFirstLetter(t('voting-protocol'))}`,
                    highlightSubString: `${capitalizeFirstLetter(t('voting-protocol'))}`,
                    type: 'link',
                    linkUrl: `${voteData?.soubor}`
                });
            }

            const transformedMemberGroupsData: { [key: string]: any } = {};
            Object.keys(voteData?.sumarizace_po_stranach).forEach((option) => {
                transformedMemberGroupsData[option] = voteData?.sumarizace_po_stranach[option].map(
                    (party: {
                        pocet: number;
                        strana_barva: string;
                        strana_zkratka: string;
                        strana_id: number;
                        lide: { id: number; jmeno: string; prijmeni: string; pohlavi: 0 | 1 }[];
                    }) => ({
                        count: party.pocet,
                        partyColor: party.strana_barva,
                        partyName: party.strana_zkratka,
                        partyId: party.strana_id,
                        members: party.lide.map(
                            (member: { id: number; jmeno: string; prijmeni: string; pohlavi: 0 | 1 }) => ({
                                id: member.id,
                                firstName: member.jmeno,
                                lastName: member.prijmeni,
                                gender: member.pohlavi
                            })
                        )
                    })
                );
            });

            return (
                <>
                    <Intro
                        title={`${t('voting-no')} ${voteData.cislo_hlasovani}`}
                        subtitle={`${t('meeting-no')} ${voteData.cislo_zasedani}`}
                        description={`${voteData.predmet_hlasovani}`}
                    />
                    <SummarySection summaryItems={summarySectionItems} />
                    <Section title={t('votings-summary')} subtitle={t('tooltip-chart')}>
                        <ChartComponent
                            type='bar'
                            data={summaryVotesData}
                            options={stackedOptions}
                            height='200px'
                            width='100%'
                            xTitle={undefined}
                            yTitle={undefined}
                            aria={undefined}
                        />
                        {datasetsDataSummaryVotes.map((option, index) => {
                            // temporary hardcoded solution to get more saturated colors
                            let saturatedColor: string = option.backgroundColor;
                            if (saturatedColor === '#b3eeb3') {
                                saturatedColor = '#66cc66';
                            } else if (saturatedColor === '#ffb3b3') {
                                saturatedColor = '#f56262';
                            } else if (saturatedColor === '#ffffb3') {
                                saturatedColor = '#f39c11';
                            } else if (saturatedColor === '#c2c2c2') {
                                saturatedColor = '#a0a0a0';
                            } else if (saturatedColor === '#d9d9d9') {
                                saturatedColor = '#b3b3b3';
                            } else if (saturatedColor === '#e8e8e8') {
                                saturatedColor = '#d9d9d9';
                            } else if (saturatedColor === '#5f5f5f') {
                                saturatedColor = '#333333';
                            }

                            return (
                                <MemberCardWrapper
                                    key={index}
                                    groupName={capitalizeFirstLetter(option.label)}
                                    tabIndex={index}
                                    count={transformedMemberGroupsData[option.label.toLowerCase()]?.reduce(
                                        (total: number, party: { count: number }) => total + party.count,
                                        0
                                    )}
                                    bgColor={saturatedColor}
                                    expanded={activeTab === index}
                                    memberGroupsData={transformedMemberGroupsData[option.label.toLowerCase()]}
                                ></MemberCardWrapper>
                            );
                        })}
                    </Section>
                </>
            );
        }
    }
};

export default VotingDetail;
